import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, ArrowRight, ArrowLeft, User, Briefcase, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import usePageTitle from "@/hooks/usePageTitle";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const serviceTypes = [
  { value: "site_web", label: "Creation de Site Web" },
  { value: "seo", label: "Referencement SEO" },
  { value: "ecommerce", label: "Site E-Commerce" },
  { value: "maintenance", label: "Maintenance & Support" },
  { value: "evenementiel", label: "Solutions evenementielles" },
  { value: "autre", label: "Autre" },
];

const budgetRanges = [
  { value: "moins_1500", label: "Moins de 1 500 EUR" },
  { value: "1500_3000", label: "1 500 - 3 000 EUR" },
  { value: "3000_5000", label: "3 000 - 5 000 EUR" },
  { value: "5000_10000", label: "5 000 - 10 000 EUR" },
  { value: "plus_10000", label: "Plus de 10 000 EUR" },
  { value: "non_defini", label: "A definir" },
];

const STEPS = [
  { id: 1, label: "Informations", icon: User },
  { id: 2, label: "Projet", icon: Briefcase },
  { id: 3, label: "Description", icon: FileText },
];

export default function ContactPage() {
  usePageTitle("Contact");
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "",
    budget: "",
    message: "",
    rgpd: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!formData.name.trim()) { toast.error("Le nom est requis"); return false; }
      if (!formData.email.trim()) { toast.error("L'email est requis"); return false; }
      return true;
    }
    if (s === 2) {
      if (!formData.service_type) { toast.error("Selectionnez un type de service"); return false; }
      return true;
    }
    if (s === 3) {
      if (!formData.message.trim()) { toast.error("Decrivez votre projet"); return false; }
      if (!formData.rgpd) { toast.error("Veuillez accepter la politique de confidentialite"); return false; }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 3));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service_type: formData.service_type,
        budget: formData.budget,
        message: formData.message,
      });
      setSubmitted(true);
      toast.success("Votre demande a ete envoyee avec succes !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez reessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="contact-success">
        <div className="text-center space-y-6 p-8">
          <div className="w-20 h-20 bg-[#0099CC] rounded-full mx-auto flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl uppercase tracking-tight text-slate-900">Message Envoye !</h1>
          <p className="text-slate-600 max-w-md">
            Merci pour votre demande. Notre equipe vous contactera dans les plus brefs delais.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setStep(1);
              setFormData({ name: "", email: "", phone: "", company: "", service_type: "", budget: "", message: "", rgpd: false });
            }}
            className="bg-[#0099CC] text-white hover:bg-[#0088B5] uppercase tracking-widest font-bold"
            data-testid="new-request-button"
          >
            Nouvelle Demande
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Header */}
      <section className="bg-white border-b border-slate-200 py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-[#0099CC] uppercase tracking-[0.3em] text-sm font-semibold">Contact</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase mt-4 mb-6 text-slate-900" data-testid="contact-title">
              Parlons de votre <span className="text-[#0099CC]">Projet</span>
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed">
              Remplissez le formulaire ci-dessous ou contactez-nous directement. Nous vous repondrons sous 24h.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-xl uppercase tracking-tight mb-6 text-slate-900">Coordonnees</h2>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0099CC]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-[#0099CC]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Adresse</h3>
                      <p className="text-slate-600 text-sm">27 Avenue Andre Rouy<br />94350 Villiers-sur-Marne</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0099CC]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-[#0099CC]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Telephone</h3>
                      <a href="tel:0601150441" className="text-[#0099CC] hover:underline">06 01 15 04 41</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0099CC]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-[#0099CC]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a href="mailto:contactac@glotechdigital.fr" className="text-[#0099CC] hover:underline">contactac@glotechdigital.fr</a>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-slate-900 p-8">
                <p className="text-[#0099CC] font-bold uppercase tracking-widest text-xl mb-2">JE VEUX.</p>
                <p className="text-[#0099CC] font-bold uppercase tracking-widest text-xl mb-2">JE PEUX.</p>
                <p className="text-[#0099CC] font-bold uppercase tracking-widest text-xl">JE FAIS.</p>
              </div>
            </div>

            {/* Multi-Step Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-slate-200 p-8 md:p-12">
                <h2 className="text-xl uppercase tracking-tight mb-6 text-slate-900">Demande de Devis</h2>

                {/* Progress Bar */}
                <div className="flex items-center justify-between mb-10" data-testid="progress-bar">
                  {STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const isActive = step === s.id;
                    const isDone = step > s.id;
                    return (
                      <div key={s.id} className="flex items-center flex-1">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                            isDone ? "bg-[#10B981] text-white" : isActive ? "bg-[#0099CC] text-white" : "bg-slate-200 text-slate-500"
                          }`}>
                            {isDone ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                          </div>
                          <span className={`text-xs mt-2 font-semibold uppercase tracking-wider ${
                            isActive ? "text-[#0099CC]" : isDone ? "text-[#10B981]" : "text-slate-400"
                          }`}>{s.label}</span>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-3 mt-[-20px] ${isDone ? "bg-[#10B981]" : "bg-slate-200"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                <form onSubmit={handleSubmit} data-testid="contact-form">
                  {/* Step 1: Personal Info */}
                  {step === 1 && (
                    <div className="space-y-6 animate-fade-in" data-testid="step-1">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="uppercase tracking-widest text-xs text-slate-700">Nom complet *</Label>
                          <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Jean Dupont" className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white placeholder:text-slate-400" data-testid="input-name" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="uppercase tracking-widest text-xs text-slate-700">Email *</Label>
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="jean@entreprise.fr" className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white placeholder:text-slate-400" data-testid="input-email" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="uppercase tracking-widest text-xs text-slate-700">Telephone</Label>
                          <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="06 12 34 56 78" className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white placeholder:text-slate-400" data-testid="input-phone" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company" className="uppercase tracking-widest text-xs text-slate-700">Entreprise</Label>
                          <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Nom de votre entreprise" className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white placeholder:text-slate-400" data-testid="input-company" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Project Details */}
                  {step === 2 && (
                    <div className="space-y-6 animate-fade-in" data-testid="step-2">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="uppercase tracking-widest text-xs text-slate-700">Type de service *</Label>
                          <Select value={formData.service_type} onValueChange={(v) => handleSelectChange("service_type", v)}>
                            <SelectTrigger className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white" data-testid="select-service">
                              <SelectValue placeholder="Selectionnez un service" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceTypes.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="uppercase tracking-widest text-xs text-slate-700">Budget estime</Label>
                          <Select value={formData.budget} onValueChange={(v) => handleSelectChange("budget", v)}>
                            <SelectTrigger className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white" data-testid="select-budget">
                              <SelectValue placeholder="Selectionnez une fourchette" />
                            </SelectTrigger>
                            <SelectContent>
                              {budgetRanges.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Message + RGPD */}
                  {step === 3 && (
                    <div className="space-y-6 animate-fade-in" data-testid="step-3">
                      <div className="space-y-2">
                        <Label htmlFor="message" className="uppercase tracking-widest text-xs text-slate-700">Decrivez votre projet *</Label>
                        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Decrivez votre projet, vos besoins et vos objectifs..." className="border-2 border-slate-200 focus:border-[#0099CC] text-slate-900 bg-white placeholder:text-slate-400 min-h-[180px]" data-testid="input-message" />
                      </div>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" id="rgpd" name="rgpd" checked={formData.rgpd} onChange={handleChange} className="mt-1 accent-[#0099CC]" data-testid="rgpd-checkbox" />
                        <label htmlFor="rgpd" className="text-xs text-slate-600">
                          J'accepte que mes donnees soient traitees conformement a la{" "}
                          <a href="/politique-confidentialite" target="_blank" className="text-[#0099CC] hover:underline">politique de confidentialite</a>.
                          Je peux demander la suppression de mes donnees a tout moment. *
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                    {step > 1 ? (
                      <Button type="button" onClick={prevStep} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 uppercase tracking-widest font-bold" data-testid="prev-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Precedent
                      </Button>
                    ) : <div />}

                    {step < 3 ? (
                      <Button type="button" onClick={nextStep} className="bg-[#0099CC] text-white hover:bg-[#0088B5] uppercase tracking-widest font-bold px-8" data-testid="next-btn">
                        Suivant
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button type="submit" disabled={loading} className="bg-[#0099CC] text-white hover:bg-[#0088B5] uppercase tracking-widest font-bold px-10 py-6" data-testid="submit-button">
                        {loading ? (
                          <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Envoi en cours...</>
                        ) : (
                          <><Send className="mr-2 h-5 w-5" />Envoyer ma demande</>
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
